import React from 'react';
import { selectorCards } from '../data/mock';
import { Zap, Handshake, Briefcase, Users, ArrowLeft } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useParallax from '../hooks/useParallax';

const iconMap = {
  Zap: Zap,
  Handshake: Handshake,
  Briefcase: Briefcase,
  Users: Users,
};

const SelectorSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.1 });
  
  // Parallax effect for background
  const parallaxBg = useParallax(0.08, 'down');

  return (
    <section id="solutions" className="bg-[#F8EEF2] py-24 relative">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-[#C5366A]/5 rounded-full blur-2xl" />
      <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#3D4B8C]/5 rounded-full blur-2xl" />

      <div className="max-w-[1400px] mx-auto px-[7.6923%] relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          dir="rtl"
        >
          <h2 className="text-[#2D2D2D] text-3xl md:text-4xl lg:text-5xl font-semibold mb-6">
            בחר את המסלול שמתאים לך
          </h2>
          <p className="text-[#6B6B6B] text-lg md:text-xl max-w-2xl mx-auto">
            שלושה מסלולים, יעד אחד - עצמאות דיגיטלית
          </p>
        </div>

        {/* Cards Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {selectorCards.map((card, index) => {
            const IconComponent = iconMap[card.icon];
            return (
              <div
                key={card.id}
                className={`bg-white border border-[#C5366A]/10 p-8 rounded-xl transition-all duration-700 ease-out hover:border-[#C5366A]/30 hover:shadow-xl group ${
                  cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: cardsVisible ? `${index * 150}ms` : '0ms' }}
              >
                <div dir="rtl" className="text-right">
                  {/* Icon */}
                  <div className="w-14 h-14 flex items-center justify-center bg-[#C5366A]/10 rounded-lg mb-6">
                    <IconComponent className="w-7 h-7 text-[#C5366A]" />
                  </div>

                  {/* Title */}
                  <h3 className="text-[#2D2D2D] text-2xl font-semibold mb-4">
                    {card.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#6B6B6B] text-base leading-relaxed mb-8 min-h-[80px]">
                    {card.description}
                  </p>

                  {/* CTA Button */}
                  <a
                    href={card.link}
                    className="inline-flex items-center justify-between gap-3 w-full bg-[#C5366A] text-white px-6 py-4 text-base font-medium rounded-lg hover:bg-[#A12D56] transition-all duration-400 ease-in-out"
                  >
                    <span>{card.ctaText}</span>
                    <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SelectorSection;
